import CryptoJS from 'crypto-js';

class EnhancedSecurity {
  static async generateAdvancedFingerprint() {
    try {
      const piiPromise = import('@fingerprintjs/fingerprintjs')
        .then(FingerprintJS => FingerprintJS.load());
      
      const pii = await piiPromise;
      const result = await pii.get();
      
      const [
        canvasFingerprint,
        webGLFingerprint
      ] = await Promise.all([
        this.getCanvasFingerprint(),
        this.getWebGLFingerprint()
      ]);
      
      const screenInfo = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const languages = navigator.languages ? navigator.languages.join(',') : navigator.language;
      const hardwareConcurrency = navigator.hardwareConcurrency || 'unknown';
      const platform = navigator.platform || 'unknown';
      const userAgent = navigator.userAgent.substring(0, 100);
      
      const combinedSignals = [
        result.visitorId,
        screenInfo,
        timeZone,
        languages,
        hardwareConcurrency,
        platform,
        userAgent,
        canvasFingerprint,
        webGLFingerprint
      ].join('|');
      
      return this.hashData(combinedSignals);
    } catch (error) {
      return this.generateBasicFingerprint();
    }
  }
  
  static async getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = 200;
      canvas.height = 50;
      
      ctx.textBaseline = "top";
      ctx.font = "14px 'Arial'";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("PiiOracle", 2, 15);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillText("PiiOracle", 4, 17);
      
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, "red");
      gradient.addColorStop(0.5, "blue");
      gradient.addColorStop(1.0, "green");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 25, 200, 25);
      
      const dataURL = canvas.toDataURL();
      return this.hashData(dataURL.substring(0, 100));
    } catch (e) {
      return 'canvas-unsupported';
    }
  }
  
  static async getWebGLFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return 'webgl-unsupported';
      
      let rendererInfo = 'unknown';
      try {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
          rendererInfo = `${vendor}~${renderer}`;
        }
      } catch (e) {
        rendererInfo = 'debug-info-error';
      }
      
      const extensions = gl.getSupportedExtensions() || [];
      const extensionsHash = this.hashData(extensions.join(','));
      
      const parameters = [
        gl.getParameter(gl.RED_BITS),
        gl.getParameter(gl.GREEN_BITS),
        gl.getParameter(gl.BLUE_BITS),
        gl.getParameter(gl.ALPHA_BITS),
        gl.getParameter(gl.DEPTH_BITS),
        gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
        gl.getParameter(gl.MAX_TEXTURE_SIZE)
      ];
      
      return `${rendererInfo}|${extensionsHash}|${parameters.join(',')}`;
    } catch (e) {
      return 'webgl-error';
    }
  }
  
  /**
   * Get audio fingerprint
   * @returns {Promise<string>} Audio fingerprint
   */
  // Removed redundant audio fingerprinting method
  
  /**
   * Get network information fingerprint
   * @returns {Promise<string>} Network fingerprint
   */
  // Removed redundant network fingerprinting method
  
  /**
   * Get battery fingerprint
   * @returns {Promise<string>} Battery fingerprint
   */
  // Removed redundant battery fingerprinting method
  
  /**
   * Get local IP address fingerprint using WebRTC
   * @returns {Promise<string>} Local IP fingerprint
   */
  // Removed redundant IP fingerprinting method
  
  /**
   * Generate a basic fingerprint as fallback
   * @returns {string} Basic fingerprint hash
   */
  static generateBasicFingerprint() {
    const screenInfo = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    const timezoneOffset = new Date().getTimezoneOffset();
    const browserInfo = navigator.userAgent.substring(0, 100);
    const languages = navigator.languages ? navigator.languages.join(',') : navigator.language;
    const platform = navigator.platform || 'unknown';
    
    return this.hashData(`${screenInfo}|${timezoneOffset}|${browserInfo}|${languages}|${platform}`);
  }
  
  /**
   * Hash data using SHA-256
   * @param {string} data Data to hash
   * @returns {string} Hashed data
   */
  static hashData(data) {
    try {
      return CryptoJS.SHA256(data).toString();
    } catch (error) {
      // Fallback to simple hash
      return this.simpleHash(data);
    }
  }
  
  /**
   * Simple hash function as fallback
   * @param {string} str String to hash
   * @returns {string} Hashed string
   */
  static simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }
  
  /**
   * Encrypt data with AES
   * @param {string} data Data to encrypt
   * @param {string} key Encryption key
   * @returns {string} Encrypted data
   */
  static encryptData(data, key) {
    try {
      return CryptoJS.AES.encrypt(data, key).toString();
    } catch (error) {
      // Fallback to base64 encoding if encryption fails
      return btoa(unescape(encodeURIComponent(data)));
    }
  }
  
  /**
   * Decrypt AES encrypted data
   * @param {string} encryptedData Encrypted data
   * @param {string} key Encryption key
   * @returns {string} Decrypted data
   */
  static decryptData(encryptedData, key) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, key);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      // Fallback to base64 decoding if decryption fails
      try {
        return decodeURIComponent(escape(atob(encryptedData)));
      } catch (e) {
        return '';
      }
    }
  }
  
  /**
   * Generate a secure random string
   * @param {number} length Length of the random string
   * @returns {string} Random string
   */
  static generateRandomString(length = 16) {
    try {
      return CryptoJS.lib.WordArray.random(length).toString();
    } catch (error) {
      // Fallback to basic random string
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      const randomValues = new Uint8Array(length);
      
      // Use crypto.getRandomValues if available
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(randomValues);
        for (let i = 0; i < length; i++) {
          result += chars.charAt(randomValues[i] % chars.length);
        }
      } else {
        // Fallback to Math.random
        for (let i = 0; i < length; i++) {
          result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
      }
      
      return result;
    }
  }
}

export default EnhancedSecurity;